import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import * as ReactDOM from 'react-dom'

import { PublicClassNames } from 'embed/constants'

import ThemeConsumer from 'embed/components/ThemeConsumer'

const styles = require('./styles')

export interface UnthemedModalProps {
  children: React.ReactNode
  className?: string
  overlayClassName?: string
  offset?: number
  onDismiss?: (event: React.MouseEvent<Element>) => void
  showHeader?: boolean
}

export interface ModalProps extends UnthemedModalProps {
  borderRadiusSmall: string
}

export interface State {
  offset?: number
}

const BODY_BOTTOM_OFFSET = 40

const closeIcon = (
  <svg data-test-id="modal_closeButton" className={styles.closeIcon} viewBox="0 0 48 48">
    <g fillRule="evenodd">
      <path d="M24 48c13.255 0 24-10.745 24-24S37.255 0 24 0 0 10.745 0 24s10.745 24 24 24zm0-3c11.598 0 21-9.402 21-21S35.598 3 24 3 3 12.402 3 24s9.402 21 21 21z" />
      <path d="M24.34 22.22l-7.775-7.775a1.5 1.5 0 1 0-2.12 2.12l7.773 7.775-7.774 7.775a1.5 1.5 0 1 0 2.12 2.12l7.775-7.773 7.774 7.774a1.5 1.5 0 1 0 2.12-2.12L26.46 24.34l7.774-7.774a1.5 1.5 0 1 0-2.12-2.12l-7.776 7.773z" />
    </g>
  </svg>
)

export class Modal extends React.Component<ModalProps, State> {
  state = { offset: 0 }

  container?: HTMLDivElement
  contentContainer: React.RefObject<HTMLDivElement>

  constructor(props) {
    super(props)
    this.contentContainer = React.createRef()
  }

  componentWillUnmount() {
    window.removeEventListener('keyup', this.onWindowKeyUp)
    this.removeDomNode()
  }

  componentDidMount() {
    this.setState({ offset: this.props.offset })
    window.addEventListener('keyup', this.onWindowKeyUp)
    this.reposition()
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.offset !== prevState.offset) {
      this.reposition()
    }
  }

  getDomNode = () => {
    if (this.container === undefined) {
      this.container = document.createElement('div')
      document.body.appendChild(this.container)
    }

    return this.container
  }

  removeDomNode = () => {
    if (this.container) {
      this.container.parentNode?.removeChild(this.container)
    }
  }

  reposition = () => {
    const bodyHeight = document.body.clientHeight
    const modalHeight = this.contentContainer.current?.clientHeight

    if (modalHeight !== undefined && this.state.offset + modalHeight + BODY_BOTTOM_OFFSET > bodyHeight) {
      this.setState({ offset: Math.max(bodyHeight - modalHeight - BODY_BOTTOM_OFFSET * 2, 0) })
    }
  }

  onWindowKeyUp = (event) => {
    const { onDismiss } = this.props
    if (event.which === 27 && onDismiss) {
      onDismiss(event)
    }
  }

  render() {
    const { borderRadiusSmall, children, className, overlayClassName, onDismiss, showHeader }: ModalProps = this.props
    const { offset } = this.state

    const closeButton = showHeader ? (
      <div
        className={classnames(
          styles.header,
          css(`border-top-right-radius: ${borderRadiusSmall};border-top-left-radius: ${borderRadiusSmall};`),
        )}
      >
        <span className={styles.closeButton} onClick={onDismiss}>
          {closeIcon}
        </span>
      </div>
    ) : (
      <span className={styles.closeButton} onClick={onDismiss}>
        {closeIcon}
      </span>
    )

    return ReactDOM.createPortal(
      <div className={styles.container}>
        <div className={classnames(styles.overlay, overlayClassName)} onClick={onDismiss} />
        <div className={styles.offsetContainer} style={{ marginTop: offset }}>
          {onDismiss && closeButton}
          <div
            className={classnames(
              PublicClassNames.Modal,
              styles.content,
              { [styles.withHeader]: showHeader },
              css(`border-radius: ${borderRadiusSmall};`),
              className,
            )}
            ref={this.contentContainer}
          >
            {children}
          </div>
        </div>
      </div>,
      this.getDomNode(),
    )
  }
}

export default (props: UnthemedModalProps) => (
  <ThemeConsumer>{({ borderRadiusSmall }) => <Modal borderRadiusSmall={borderRadiusSmall} {...props} />}</ThemeConsumer>
)
